import { ICashFlowFLI, ICashFlowInvestingFLI } from '@modeso/aumico-lib-annualreport-fe';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IAccountMappingTemplateGroupItemFLI,
    ICashFlowStatement,
    ICashFlowStatementEntry,
    ICashFlowStatementInvestingEntry,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemCashFlowType } from '@modeso/types__aumico-lib-settings-be';
import {
    ITemplateEntityDTO,
    ITemplateGroupEntityDTO,
    ITemplateGroupItemEntityDTO,
} from '@modeso/types__aumico-lib-templates-be';
import { cloneDeep } from 'lodash-es';
import { IStringDictionary } from '../../../interfaces/IStringDictionary.interface';

export class CashFlowStatementHelper {
    private static previousAnnualReportCashFlowStatement: ICashFlowStatement;

    // Used when there is previous annual report (autofill)

    public static prepareCashFlowFlisFromPreviousCashFlowStatement(
        previousAnnualReportCashFlowStatement: ICashFlowStatement,
        balanceFlis: IAccountMappingTemplateGroupItemFLI[],
        flisCashFlowDictionary: { [name: string]: string },
    ): ICashFlowFLI[] {
        let balanceFliNameToFliMap = new Map<string, IAccountMappingTemplateGroupItemFLI>();

        balanceFlis.forEach((fli) => {
            balanceFliNameToFliMap.set(fli.name, fli);
        });

        this.previousAnnualReportCashFlowStatement = cloneDeep(previousAnnualReportCashFlowStatement);
        const operatingActivitiesFlis: ICashFlowFLI[] = [];
        const investingActivitiesFlis: ICashFlowFLI[] = [];
        const financingActivitiesFlis: ICashFlowFLI[] = [];

        const allFlis: ICashFlowFLI[] = [];

        previousAnnualReportCashFlowStatement.operatingActivities.entries.forEach((previousEntry) => {
            const fli = this.prepareActivitiesFli(previousEntry, balanceFliNameToFliMap, flisCashFlowDictionary);
            if (fli && fli.isCustom) {
                fli.fliCashFlow = FinancialLineItemCashFlowType.OPERATIONS;
            }
            if (
                fli != null &&
                (fli.fliCashFlow === FinancialLineItemCashFlowType.OPERATIONS ||
                    fli.fliCashFlow == FinancialLineItemCashFlowType.NETWORKING_CAPITAL)
            ) {
                operatingActivitiesFlis.push(fli);
            }
        });

        previousAnnualReportCashFlowStatement.investingActivities.entries.forEach((previousEntry) => {
            const fli = this.prepareInvestingActivitiesFli(
                previousEntry,
                balanceFliNameToFliMap,
                flisCashFlowDictionary,
            );
            if (fli && fli.isCustom) {
                fli.fliCashFlow = FinancialLineItemCashFlowType.INVESTING;
            }
            if (fli != null && fli.fliCashFlow === FinancialLineItemCashFlowType.INVESTING) {
                investingActivitiesFlis.push(fli);
            }
        });

        previousAnnualReportCashFlowStatement.financingActivities.entries.forEach((previousEntry) => {
            const fli = this.prepareActivitiesFli(previousEntry, balanceFliNameToFliMap, flisCashFlowDictionary);
            if (fli && fli.isCustom) {
                fli.fliCashFlow = FinancialLineItemCashFlowType.FINANCING;
            }
            if (fli != null && fli.fliCashFlow === FinancialLineItemCashFlowType.FINANCING) {
                financingActivitiesFlis.push(fli);
            }
        });

        allFlis.push(...operatingActivitiesFlis, ...investingActivitiesFlis, ...financingActivitiesFlis);
        this.restoreRemovedFlisFromPreviousCashFlow(allFlis, balanceFlis, flisCashFlowDictionary);

        return allFlis;
    }

    private static prepareInvestingActivitiesFli(
        previousEntry: ICashFlowStatementInvestingEntry,
        balanceFliNameToFliMap: Map<string, IAccountMappingTemplateGroupItemFLI>,
        flisCashFlowDictionary: { [name: string]: string },
    ): ICashFlowInvestingFLI | null {
        const matchingBalanceFli = balanceFliNameToFliMap.get(previousEntry.fliId);
        let previousValue: number | null = null;

        if (
            previousEntry.calculatedCurrentValue != null &&
            NumberUtils.isFinite(previousEntry.calculatedCurrentValue)
        ) {
            previousValue = NumberUtils.roundNumber(previousEntry.calculatedCurrentValue);
        }

        if (matchingBalanceFli != null) {
            const fliWithReclass = matchingBalanceFli as unknown as { closingReClass?: number; openingReClass?: number };
            let currentValue: number | null = null;
            if (
                matchingBalanceFli.calculatedClosingBalance != null &&
                NumberUtils.isFinite(matchingBalanceFli.calculatedClosingBalance)
            ) {
                const reclass = fliWithReclass.closingReClass ?? 0;
                currentValue = NumberUtils.roundNumber(matchingBalanceFli.calculatedClosingBalance + reclass);
            }

            let openingValue: number | null = null;
            if (
                matchingBalanceFli.calculatedOpeningBalance != null &&
                NumberUtils.isFinite(matchingBalanceFli.calculatedOpeningBalance)
            ) {
                const reclass = fliWithReclass.openingReClass ?? 0;
                openingValue = NumberUtils.roundNumber(matchingBalanceFli.calculatedOpeningBalance + reclass);
            }
            return {
                id: matchingBalanceFli.name,
                caption: previousEntry?.name ?? matchingBalanceFli.caption,
                fliType: matchingBalanceFli.fliType,
                fliCashFlow: matchingBalanceFli.cashFlowType ?? +flisCashFlowDictionary[matchingBalanceFli.name],
                currentValue,
                openingValue,
                // previous values for flis
                previousValue,
                investingPreviousValue: this.getInvestingPreviousValue(previousEntry),
                disinvestingPreviousValue: this.getDisinvestingPreviousValue(previousEntry),
                parentFli:
                    'parentFli' in previousEntry ? (previousEntry as ICashFlowStatementInvestingEntry).parentFli : null,
            };
            // If there is no matching fli this means it was either removed by user or its a custom fli added by user
        } else {
            // custom fli

            return {
                id: !previousEntry.parentFli ? previousEntry.fliId : null,
                caption: previousEntry.name,
                fliType: null,
                fliCashFlow: FinancialLineItemCashFlowType.INVESTING,
                currentValue: null,
                openingValue: null,
                previousValue,
                investingPreviousValue: this.getInvestingPreviousValue(
                    previousEntry as ICashFlowStatementInvestingEntry,
                ),
                disinvestingPreviousValue: this.getDisinvestingPreviousValue(
                    previousEntry as ICashFlowStatementInvestingEntry,
                ),
                isCustom: true,
                parentFli: previousEntry.parentFli,
            };
        }
    }

    private static prepareActivitiesFli(
        previousEntry: ICashFlowStatementEntry,
        balanceFliNameToFliMap: Map<string, IAccountMappingTemplateGroupItemFLI>,
        flisCashFlowDictionary: { [name: string]: string },
    ): ICashFlowFLI | ICashFlowInvestingFLI | null {
        const matchingBalanceFli = balanceFliNameToFliMap.get(previousEntry.fliId);
        let previousValue: number | null = null;

        if (previousEntry.manuallyEnteredCurrentValue != null && NumberUtils.isFinite(previousEntry.manuallyEnteredCurrentValue)) {
            previousValue = NumberUtils.roundNumber(previousEntry.manuallyEnteredCurrentValue);
        }
        else if (
            previousEntry.calculatedCurrentValue != null &&
            NumberUtils.isFinite(previousEntry.calculatedCurrentValue)
        ) {
            previousValue = NumberUtils.roundNumber(previousEntry.calculatedCurrentValue);
        }

        if (matchingBalanceFli != null) {
            const fliWithReclass = matchingBalanceFli as unknown as { closingReClass?: number; openingReClass?: number };
            let currentValue: number | null = null;
            if (
                matchingBalanceFli.calculatedClosingBalance != null &&
                NumberUtils.isFinite(matchingBalanceFli.calculatedClosingBalance)
            ) {
                const reclass = fliWithReclass.closingReClass ?? 0;
                currentValue = NumberUtils.roundNumber(matchingBalanceFli.calculatedClosingBalance + reclass);
            }

            let openingValue: number | null = null;
            if (
                matchingBalanceFli.calculatedOpeningBalance != null &&
                NumberUtils.isFinite(matchingBalanceFli.calculatedOpeningBalance)
            ) {
                const reclass = fliWithReclass.openingReClass ?? 0;
                openingValue = NumberUtils.roundNumber(matchingBalanceFli.calculatedOpeningBalance + reclass);
            }
            return {
                id: matchingBalanceFli.name,
                caption: previousEntry?.name ?? matchingBalanceFli.caption,
                fliType: matchingBalanceFli.fliType,
                fliCashFlow: matchingBalanceFli.cashFlowType ?? +flisCashFlowDictionary[matchingBalanceFli.name],
                currentValue,
                openingValue,
                // previous values for flis
                previousValue,
                investingPreviousValue: this.getInvestingPreviousValue(
                    previousEntry as ICashFlowStatementInvestingEntry,
                ),
                disinvestingPreviousValue: this.getDisinvestingPreviousValue(
                    previousEntry as ICashFlowStatementInvestingEntry,
                ),
                parentFli:
                    'parentFli' in previousEntry ? (previousEntry as ICashFlowStatementInvestingEntry).parentFli : null,
            };
            // If there is no matching fli this means it was either removed by user or its a custom fli added by user
        } else if (previousEntry.isCustom) {
            // custom fli

            return {
                id: 'parentFli' in previousEntry ? previousEntry.fliId : null,
                caption: previousEntry.name,
                fliType: null,
                fliCashFlow: null,
                currentValue: null,
                openingValue: null,
                previousValue,
                investingPreviousValue: this.getInvestingPreviousValue(
                    previousEntry as ICashFlowStatementInvestingEntry,
                ),
                disinvestingPreviousValue: this.getDisinvestingPreviousValue(
                    previousEntry as ICashFlowStatementInvestingEntry,
                ),
                isCustom: true,
                parentFli:
                    'parentFli' in previousEntry ? (previousEntry as ICashFlowStatementInvestingEntry).parentFli : null,
            };
        }

        return null;
    }
    private static getInvestingPreviousValue(parentEntry: ICashFlowStatementInvestingEntry): number | null {
        const childInvestingEntry = this.previousAnnualReportCashFlowStatement.investingActivities.entries.find(
            (entry) => {
                if (parentEntry.isCustom) {
                    return entry.investing && entry.parentFli === parentEntry.name;
                } else {
                    return entry.investing && entry.parentFli === parentEntry.fliId;
                }
            },
        );

        let investingPreviousValue: number | null = null;
        if (
            childInvestingEntry != null &&
            childInvestingEntry.calculatedCurrentValue != null &&
            NumberUtils.isFinite(childInvestingEntry.calculatedCurrentValue)
        ) {
            investingPreviousValue = NumberUtils.roundNumber(childInvestingEntry.calculatedCurrentValue);
        }

        return investingPreviousValue;
    }

    private static getDisinvestingPreviousValue(parentEntry: ICashFlowStatementInvestingEntry): number | null {
        const childDisInvestingEntry = this.previousAnnualReportCashFlowStatement.investingActivities.entries.find(
            (entry) => {
                if (parentEntry.isCustom) {
                    return entry.disinvesting && entry.parentFli === parentEntry.name;
                } else {
                    return entry.disinvesting && entry.parentFli === parentEntry.fliId;
                }
            },
        );

        let disinvestingPreviousValue: number | null = null;
        if (
            childDisInvestingEntry != null &&
            childDisInvestingEntry.calculatedCurrentValue != null &&
            NumberUtils.isFinite(childDisInvestingEntry.calculatedCurrentValue)
        ) {
            disinvestingPreviousValue = NumberUtils.roundNumber(childDisInvestingEntry.calculatedCurrentValue);
        }

        return disinvestingPreviousValue;
    }

    private static restoreRemovedFlisFromPreviousCashFlow(
        newFlis: ICashFlowFLI[],
        balanceFlis: IAccountMappingTemplateGroupItemFLI[],
        flisCashFlowDictionary: { [name: string]: string },
    ): void {
        balanceFlis.forEach((fli) => {
            const isFliFound = newFlis.find((newFli) => newFli.id === fli.name);
            if (isFliFound == null) {
                const fliWithReclass = fli as unknown as { closingReClass?: number; openingReClass?: number };
                let currentValue: number | null = null;
                if (fli.calculatedClosingBalance != null && NumberUtils.isFinite(fli.calculatedClosingBalance)) {
                    const reclass = fliWithReclass.closingReClass ?? 0;
                    currentValue = NumberUtils.roundNumber(fli.calculatedClosingBalance + reclass);
                }

                let openingValue: number | null = null;
                if (fli.calculatedOpeningBalance != null && NumberUtils.isFinite(fli.calculatedOpeningBalance)) {
                    const reclass = fliWithReclass.openingReClass ?? 0;
                    openingValue = NumberUtils.roundNumber(fli.calculatedOpeningBalance + reclass);
                }

                const cashFlowType = fli.cashFlowType ?? +flisCashFlowDictionary[fli.name];
                newFlis.push({
                    id: fli.name,
                    caption: fli.caption,
                    fliType: fli.fliType,
                    fliCashFlow: cashFlowType,
                    currentValue,
                    openingValue,
                    // previous values for flis
                    previousValue: null,
                });
            }
        });
    }

    // Used when there is no previous annual report (no autofill)

    public static prepareCashFlowFli(
        balanceFli: IAccountMappingTemplateGroupItemFLI,
        flisCashFlowDictionary: { [name: string]: string },
    ): ICashFlowFLI | ICashFlowInvestingFLI {
        const fliWithReclass = balanceFli as unknown as { closingReClass?: number; openingReClass?: number };
        let currentValue: number | null = null;
        if (balanceFli.calculatedClosingBalance != null && NumberUtils.isFinite(balanceFli.calculatedClosingBalance)) {
            const reclass = fliWithReclass.closingReClass ?? 0;
            currentValue = NumberUtils.roundNumber(balanceFli.calculatedClosingBalance + reclass);
        }

        let openingValue: number | null = null;
        if (balanceFli.calculatedOpeningBalance != null && NumberUtils.isFinite(balanceFli.calculatedOpeningBalance)) {
            const reclass = fliWithReclass.openingReClass ?? 0;
            openingValue = NumberUtils.roundNumber(balanceFli.calculatedOpeningBalance + reclass);
        }
        // if cash flow type is not saved in fli inside mapping, then get it from the prepared map that comes from the template from admin
        const cashflowType = balanceFli.cashFlowType ?? +flisCashFlowDictionary[balanceFli.name];

        return {
            id: balanceFli.name,
            caption: balanceFli.caption,
            fliType: balanceFli.fliType,
            fliCashFlow: cashflowType,
            currentValue,
            openingValue,
            // previous values for flis
            previousValue: null,
            // previous values for investing entries
            investingPreviousValue: null,
            // previous values for disInvesting entries
            disinvestingPreviousValue: null,
        };
    }

    public static prepareFliCashFlowDictionary(template: ITemplateEntityDTO): { [name: string]: string } {
        const flisCashFlowDictionary: { [name: string]: string } = {};

        if (template == null) {
            return flisCashFlowDictionary;
        }
        const templateGroups: ITemplateGroupEntityDTO[] = [template.group];

        this.getFlisCashFlowsOfGroups(templateGroups, flisCashFlowDictionary);

        return flisCashFlowDictionary;
    }

    private static getFlisCashFlowsOfGroups(
        templateGroups: ITemplateGroupEntityDTO[],
        flisCashFlowDictionary: { [name: string]: string },
    ): void {
        for (const group of templateGroups) {
            this.extractFLIAndCashFlow(group.items, flisCashFlowDictionary);

            if (group.groups && group.groups.length > 0) {
                this.getFlisCashFlowsOfGroups(group.groups, flisCashFlowDictionary);
            }
        }
    }

    private static extractFLIAndCashFlow(
        items: ITemplateGroupItemEntityDTO[],
        flisCashFlowDictionary: IStringDictionary,
    ): void {
        for (const item of items) {
            if (!item.isSubtotal) {
                flisCashFlowDictionary[item.name] = item.fliObj.fliObj.cashflow;
            }
        }
    }
}
